import {useId, useState} from 'react';
import {projectArchitectures} from './projectArchitectures';
import s from './index.module.less';

const palette = {entry: '#65dce5', compute: '#77dec3', data: '#b9a3ef', control: '#f0aaa7', bus: '#f6c079', external: '#a4b7cb'};
const kinds = {entry: '使用入口', compute: '计算 / 服务', data: '数据 / 证据', control: '控制 / 验证', bus: '事件流', external: '外部能力'};

// Orthogonal routing uses the gutters between rows; labels never sit on top of nodes.
const route = (a, b) => {
    const w = 254;
    const h = 86;
    if (a.y === b.y) {
        const forward = b.x > a.x;
        const start = a.x + (forward ? w : 0);
        const end = b.x + (forward ? 0 : w);
        if (Math.abs(a.x - b.x) <= 312) return {d: `M${start},${a.y + h / 2} H${end}`, x: (start + end) / 2, y: a.y + h / 2 - 9};
        const gutter = a.y - 30;
        return {d: `M${a.x + w / 2},${a.y} V${gutter} H${b.x + w / 2} V${b.y}`, x: (a.x + b.x + w) / 2, y: gutter - 7};
    }
    const forward = b.y > a.y;
    const startY = a.y + (forward ? h : 0);
    const endY = b.y + (forward ? 0 : h);
    const startX = a.x + w / 2;
    const endX = b.x + w / 2;
    const middleY = (startY + endY) / 2 + (a.x === b.x ? 0 : a.x > b.x ? 12 : -12);
    // Long cross-layer edges travel in the outer gutter, clear of intermediate nodes.
    if (Math.abs(a.y - b.y) > 170) {
        const side = a.x < 350 ? 14 : 936;
        return {d: `M${startX},${startY} V${startY + (forward ? 16 : -16)} H${side} V${endY + (forward ? -16 : 16)} H${endX} V${endY}`, x: side, y: middleY, rotation: side < 350 ? -90 : 90};
    }
    return {d: `M${startX},${startY} V${middleY} H${endX} V${endY}`, x: startX === endX ? startX + 30 : startX + (endX - startX) * .3, y: middleY - 7};
};

const ProjectArchitecture = ({card}) => {
    const graph = projectArchitectures[card.architectureKey];
    const [selectedId, setSelectedId] = useState(null);
    const [zoomed, setZoomed] = useState(false);
    const uid = useId().replace(/:/g, '');
    if (!graph) return null;
    const selected = graph.nodes.find((node) => node.id === selectedId);
    const connected = new Set(selected ? graph.edges.filter((edge) => edge.from === selected.id || edge.to === selected.id).flatMap((edge) => [edge.from, edge.to]) : []);
    const select = (id) => setSelectedId((current) => current === id ? null : id);
    const colors = [...new Set(graph.nodes.map((node) => node.kind))];
    return <section className={s.architecture} id='project-architecture' aria-labelledby={`architecture-title-${uid}`} tabIndex={-1}>
        <div className={s.architectureHead}>
            <div><span className={s.architectureEyebrow}>SYSTEM / STRUCTURE</span><h3 id={`architecture-title-${uid}`}>{graph.title}</h3></div>
            <button type='button' onClick={() => setZoomed(!zoomed)} aria-pressed={zoomed}>{zoomed ? '适应宽度' : '放大图'} <span aria-hidden='true'>↗</span></button>
        </div>
        <p className={s.architectureMode}><i aria-hidden='true' />{graph.mode}<span>点击节点，查看职责与边界</span></p>
        <div className={s.architectureViewport} tabIndex={0} role='region' aria-label='架构图画布，可横向滚动查看' data-zoom={zoomed}>
            <svg viewBox={`0 0 952 ${graph.height}`} role='group' aria-label={`${card.title}：${graph.mode}`}>
                <title>{graph.title}</title>
                <desc>{graph.notes.join(' ')}。实线箭头表示数据或调用关系，虚线箭头表示控制与验证。</desc>
                <defs>
                    <pattern id={`grid-${uid}`} width='24' height='24' patternUnits='userSpaceOnUse'><path d='M24 0H0V24' fill='none' stroke='#8298b3' strokeOpacity='.09' strokeWidth='.7'/></pattern>
                    <marker id={`arrow-${uid}`} markerWidth='8' markerHeight='8' refX='7' refY='4' orient='auto'><path d='M0 1L7 4L0 7' fill='none' stroke='#8ba3b8' strokeWidth='1.4'/></marker>
                    <marker id={`active-arrow-${uid}`} markerWidth='8' markerHeight='8' refX='7' refY='4' orient='auto'><path d='M0 1L7 4L0 7' fill='none' stroke='#c8fff0' strokeWidth='1.6'/></marker>
                </defs>
                <rect width='952' height={graph.height} fill={`url(#grid-${uid})`}/>
                {graph.layers.map((layer, index) => <g key={layer.label} className={s.architectureLayer}>
                    <rect x='22' y={layer.y} width='908' height='135' rx='3'/>
                    <text x='38' y={layer.y + 20}>{String(index + 1).padStart(2, '0')} / {layer.label}</text>
                </g>)}
                {graph.edges.map((edge, index) => {
                    const a = graph.nodes.find((node) => node.id === edge.from);
                    const b = graph.nodes.find((node) => node.id === edge.to);
                    const path = route(a, b);
                    const active = selected && (edge.from === selected.id || edge.to === selected.id);
                    return <g key={`${edge.from}-${edge.to}-${index}`} className={s.architectureEdge} data-active={active || undefined} data-muted={(selected && !active) || undefined}>
                        <path d={path.d} markerEnd={`url(#${active ? 'active-arrow' : 'arrow'}-${uid})`} strokeDasharray={edge.kind === 'control' ? '5 4' : undefined}/>
                        {edge.label ? <text x={path.x} y={path.y} textAnchor='middle' transform={path.rotation ? `rotate(${path.rotation} ${path.x} ${path.y})` : undefined}>{edge.label}</text> : null}
                    </g>;
                })}
                {graph.nodes.map((node) => <g
                    key={node.id}
                    className={s.architectureNode}
                    data-node={node.id}
                    data-selected={selected?.id === node.id || undefined}
                    data-muted={(selected && selected.id !== node.id && !connected.has(node.id)) || undefined}
                    role='button'
                    tabIndex={0}
                    aria-pressed={selected?.id === node.id}
                    aria-label={`${node.label}：${node.sub}。${node.detail}`}
                    onClick={() => select(node.id)}
                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(node.id); } }}
                    style={{'--node-color': palette[node.kind]}}
                >
                    <rect className={s.architectureNodeMask} x={node.x} y={node.y} width='254' height='86' rx='4'/>
                    <rect className={s.architectureNodeSurface} x={node.x} y={node.y} width='254' height='86' rx='4'/>
                    <path className={s.architectureNodeCorner} d={`M${node.x + 1} ${node.y + 20}V${node.y + 1}H${node.x + 20}`}/>
                    <circle cx={node.x + 19} cy={node.y + 24} r='3' fill={palette[node.kind]}/>
                    <text className={s.architectureNodeTitle} x={node.x + 30} y={node.y + 30}>{node.label}</text>
                    <text className={s.architectureNodeSub} x={node.x + 19} y={node.y + 59}>{node.sub}</text>
                </g>)}
                <g className={s.architectureLegend}>
                    {colors.map((kind, i) => <g key={kind} transform={`translate(${38 + i * 150},${graph.height - 25})`}><circle r='3' fill={palette[kind]}/><text x='11' y='4'>{kinds[kind]}</text></g>)}
                </g>
            </svg>
        </div>
        <div className={s.architectureInspector} aria-live='polite'>
            <span>{selected ? 'NODE / RESPONSIBILITY' : 'DESIGN / BOUNDARY'}</span>
            <strong>{selected ? selected.label : '关键设计'}</strong>
            <p>{selected ? selected.detail : graph.notes.join(' ')}</p>
            {selected ? <button type='button' onClick={() => setSelectedId(null)}>返回架构总览</button> : null}
        </div>
        <p className={s.architectureBasis}>系统逻辑视图</p>
    </section>;
};

export default ProjectArchitecture;
